import { useEffect, useState } from "react";
import {
  ADVICE_ABOUT_YOU_STEP,
  ADVICE_PRICE_STEP,
  ADVICE_YOUR_LIFE_SITUATION_STEP,
  PRODUCT_PATH,
} from ".";
import { useExistingCart, useIsEditMode } from "../../hooks/checkPriceHooks";
import {
  mapAdviceDataToDetailsFormData,
  mapAdviceDataToIntroFormData,
} from "./ProductDetails/utils";

import { resetAdviceState } from "Store/advice/slice";
import {
  AdviceVersion,
  useExistingAdviceVersion,
} from "Store/akaAdvice/akaAdviceHooks";
import { useCartSelector } from "Store/cart";
import { Cart } from "Store/cart/cartInterfaces";
import { useFormStateSelector } from "Store/priceCalculation/priceCalculationSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { getCartIdFromCookie } from "Utils/cookies/cookiesUtil";
import { isDefined } from "Utils/functions";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import isEmpty from "lodash.isempty";
import isEqual from "lodash.isequal";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useUserIdentification } from "../../userIdentificationHook";
import { useCheckPriceRedirectToCartCallback } from "../../utils/checkPriceUtils";
import { ProductBackendName } from "../productTypes";
import { initialFormValues } from "./ProductIntro";

export const useAdviceStartStep = (): string | undefined => {
  const history = useHistory();
  const [initialPath] = useState(history.location.pathname);
  const hasCartIdInCookies = isDefined(getCartIdFromCookie());
  const isEditMode = useIsEditMode();

  const isInitialPathPriceStep =
    initialPath === `${PRODUCT_PATH}/${ADVICE_PRICE_STEP.id}`;

  return (hasCartIdInCookies || isEditMode) && isInitialPathPriceStep
    ? ADVICE_PRICE_STEP.id
    : undefined;
};

export const useAdviceStateCleanup = (): void => {
  const dispatch = useDispatch();
  useEffect(() => {
    return () => {
      dispatch(resetAdviceState());
    };
  }, []);
};

export const useBlockAdvisorCartDialog = (): {
  open: boolean;
  closeDrawer: () => void;
} => {
  const { current: cart } = useCartSelector();
  const redirectToCart = useCheckPriceRedirectToCartCallback();
  const blockCart =
    isDefined(cart) &&
    isDefined(cart.cartOwnedByAdvisor) &&
    cart.cartOwnedByAdvisor;

  return { open: blockCart, closeDrawer: redirectToCart };
};

export const useExistingAdviceFormStates = (
  cart: Cart | null,
  setAdviceCartFetched?: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  const dispatch = useDispatch();
  const userIdentification = useUserIdentificationSelector();
  const productIntroFormState = useFormStateSelector(ADVICE_ABOUT_YOU_STEP.id);
  const productDetailsFormState = useFormStateSelector(
    ADVICE_YOUR_LIFE_SITUATION_STEP.id,
  );
  useEffect(() => {
    const customerDateOfBirth =
      userIdentification?.newCustomerResponse?.dateOfBirth;

    if (
      isDefined(cart) &&
      isDefined(cart.advice) &&
      isDefined(cart.advice.adviceProductValues) &&
      isDefined(customerDateOfBirth)
    ) {
      const { adviceProductValues } = cart.advice;

      if (
        isEmpty(productIntroFormState) ||
        isEqual(productIntroFormState, initialFormValues)
      ) {
        const { isStayingInNorwayForMoreThanFiveYears } = adviceProductValues;
        dispatch(
          updatePriceCalculationFormState({
            stepId: ADVICE_ABOUT_YOU_STEP.id,
            formState: mapAdviceDataToIntroFormData(
              isStayingInNorwayForMoreThanFiveYears,
            ),
          }),
        );
      }
      if (isEmpty(productDetailsFormState)) {
        const formData = {
          ...adviceProductValues,
          dateOfBirth: customerDateOfBirth,
        };
        dispatch(
          updatePriceCalculationFormState({
            stepId: ADVICE_YOUR_LIFE_SITUATION_STEP.id,
            formState: mapAdviceDataToDetailsFormData(formData),
          }),
        );
      }
      if (isDefined(setAdviceCartFetched)) {
        setAdviceCartFetched(true);
      }
    }
  }, [cart, userIdentification]);
};

export const useExistingAdviceCart = (): void => {
  const cartEntity = useExistingCart();
  useUserIdentification(ProductBackendName.ADVICE);
  useExistingAdviceFormStates(cartEntity.current);
};

export const useAdviceVersion = (): AdviceVersion => {
  const existingAdviceVersion = useExistingAdviceVersion();
  const toggleAdviceRefactor = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_1356_ADVICE_REFACTOR,
  );
  if (isDefined(existingAdviceVersion)) {
    return existingAdviceVersion;
  }

  return toggleAdviceRefactor ? AdviceVersion.NEW : AdviceVersion.OLD;
};
